import { memo, useState, useMemo } from "react";
import {
  Flex,
  Text,
  Grid,
  Image,
  Button,
  useMantineTheme,
  ScrollArea,
} from "@mantine/core";
import Header from "../Header";
import Textarea from "src/components/Textarea/Textarea";
import NotesList from "src/modules/Client/ClientDetail/notes";
import {
  MemberStatus,
  useCreateNotesMutation,
  useGetCheckInOneQuery,
  useGetNotesQuery,
  useUpdateCheckInMutation,
} from "src/generated/graphql";
import { useAppContext } from "src/api/AppContext";
import { CheckInQueueStatus, getStatusObj } from "..";
import { GetCheckInMany } from "src/api/graphql/queries/CheckIn";
import { EMPLOYEE_ROLE_OBJ } from "src/utils/constants";

function LeftSide() {
  const { selectedCheckInId, currentEmployee, currentLocation } =
    useAppContext();
  const [note, setnote] = useState("");
  const theme = useMantineTheme();
  let color = theme.colors.secondaryTextColor[6];

  const { data: checkInDetail, refetch } = useGetCheckInOneQuery({
    variables: { checkInOneId: selectedCheckInId },
    skip: !selectedCheckInId,
    fetchPolicy: "cache-and-network",
  });
  const { client, id, isAccepted, isOverride } =
    checkInDetail?.checkInOne || {};

  const { data: notesData, refetch: refetchNotes } = useGetNotesQuery({
    variables: { clientId: client?.id },
    skip: !client?.id,
    fetchPolicy: "cache-and-network",
  });

  const statusObj = useMemo(
    () =>
      getStatusObj(
        client?.memberships || [],
        currentLocation?.id,
        theme,
        isAccepted
      ),
    [client, currentLocation, isAccepted]
  );

  // currently selected membership
  const currentMemberShip = useMemo(
    () =>
      client?.memberships?.find((member) => member.id === statusObj.memberId),
    [statusObj, client]
  );

  const getMemberSince = (
    memberships: NonNullable<typeof client>["memberships"]
  ) => {
    if (currentLocation?.id) {
      memberships = memberships.filter(
        (m) => m.location.id === currentLocation.id
      );
    }
    let sorted = [...memberships].sort(
      (a, b) =>
        new Date(a.termStart).valueOf() - new Date(b.termStart).valueOf()
    );

    if (sorted?.length > 0) {
      return new Date(sorted[0].termStart).toLocaleDateString();
    } else {
      return undefined;
    }
  };

  const renderMembership = () => {
    if (currentMemberShip) {
      let active = currentMemberShip;

      return (
        <Flex sx={{ justifyContent: "space-between" }}>
          <Text>{active.agreement.name}</Text>
          <Text size={14} fw={500}>
            {`${active.isAutoRenew ? "Renews" : "Expires"} ${new Date(
              active.termEnd
            ).toLocaleDateString()}`}
          </Text>
        </Flex>
      );
    } else {
      return undefined;
    }
  };

  const [updateCheckIn, { loading: checkInLoading }] = useUpdateCheckInMutation(
    {
      onCompleted() {
        refetch();
      },
      refetchQueries: [
        {
          query: GetCheckInMany,
          variables: {
            locationId: currentLocation?.id,
            todayRecords: true,
          },
        },
      ],
    }
  );
  const [createNotes] = useCreateNotesMutation({
    onCompleted: () => {
      setnote("");
      refetchNotes();
    },
  });

  const handleUpdateCheckIn = (bool: boolean) => {
    if (!id || !client?.id) return;
    updateCheckIn({
      variables: {
        id,
        data: { isAccepted: bool },
      },
    });
    createNotes({
      variables: {
        data: {
          content: note,
          client: {
            id: client.id,
          },
          ...(currentEmployee && {
            author: {
              id: currentEmployee.id,
            },
          }),
        },
      },
    });
  };

  return (
    <>
      <Header checkInDetail={checkInDetail?.checkInOne} />
      <ScrollArea style={{ height: "80vh" }} type="auto">
        <Grid m={0} mb={"xs"}>
          <Grid.Col sm={12} md={6}>
            <Image
              width={"100%"}
              height={"100%"}
              mah={357}
              src={client?.user?.profilePicUrl || null}
              alt="profile Image"
              withPlaceholder
              radius={4}
              styles={{
                imageWrapper: { height: "100%" },
                figure: { height: "100%" },
                root: { height: "100%" },
              }}
            />
          </Grid.Col>

          <Grid.Col span="auto">
            <Flex
              sx={{
                background: theme.colors.secondary[6],
                flex: 1,
                padding: 20,
                borderRadius: 4,
                minHeight: 357,
              }}
              gap={7}
              direction={"column"}
            >
              {[
                CheckInQueueStatus.ExpireInMonth,
                CheckInQueueStatus.NotPaid,
              ].includes(statusObj.CheckInQueueStatus) && (
                <Text fw={600} size={18} pb={"xs"} align="center">
                  {statusObj.CheckInQueueStatus === CheckInQueueStatus.NotPaid
                    ? "Payment Not Paid yet!"
                    : "Subscription is going to expired within a month"}
                </Text>
              )}
              <Flex sx={{ justifyContent: "space-between" }}>
                <Text size={18} fw={500}>
                  {client?.memberStatus}
                  {client?.memberStatus == MemberStatus.Lead &&
                    client?.leadStatus &&
                    `- ${client.leadStatus}`}{" "}
                  -{" "}
                  {statusObj.CheckInQueueStatus === CheckInQueueStatus.Denied
                    ? currentMemberShip?.status || statusObj.CheckInQueueStatus
                    : statusObj.CheckInQueueStatus}
                </Text>
                <Text size={14} fw={500}>
                  {client?.memberships && getMemberSince(client.memberships)}
                </Text>
              </Flex>
              {client && renderMembership()}
              <Flex sx={{ flex: 1 }}>
                {client?.dob && <Text>DOB: {client.dob}</Text>}
              </Flex>

              {!isAccepted &&
                !isOverride &&
                statusObj.CheckInQueueStatus === CheckInQueueStatus.Denied && (
                  <Flex
                    direction={"column"}
                    sx={{
                      background: theme.colors.secondary[4],
                      color: theme.colors.secondaryTextColor[4],
                      borderRadius: 4,
                    }}
                    align={"center"}
                  >
                    <Text
                      bg="#D16A6A"
                      sx={{
                        width: "100%",
                        color: "#FFFFFF",
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                      }}
                      fw={500}
                      size={20}
                      align="center"
                    >
                      Access Denied
                    </Text>
                    <Text pt={"xs"} size={16}>
                      Provide a reason for overriding entry below.
                    </Text>

                    <Textarea
                      w={"80%"}
                      mt={"xs"}
                      rows={4}
                      value={note}
                      onChange={(e) => setnote(e.target.value)}
                    />

                    <Flex m={"sm"} gap={"xl"}>
                      <Button
                        radius={"xl"}
                        onClick={() => handleUpdateCheckIn(false)}
                        variant="outline"
                        disabled={!note || checkInLoading}
                        style={{
                          flex: 1,
                          color: theme.colors.secondaryTextColor[6],
                        }}
                        sx={{
                          backgroundColor: theme.colors.secondary[6],
                          "&:hover": {
                            background: theme.colors.secondary[5],
                          },
                        }}
                      >
                        Deny Entry
                      </Button>
                      <Button
                        radius={"xl"}
                        disabled={!note || checkInLoading}
                        onClick={() => handleUpdateCheckIn(true)}
                        style={{
                          flex: 1,
                        }}
                        sx={{
                          color: theme.colors.primaryTextColor[6],
                          "&:hover": {
                            backgroundColor: theme.colors.primary[4],
                          },
                        }}
                      >
                        Allow Entry
                      </Button>
                    </Flex>
                  </Flex>
                )}
            </Flex>
          </Grid.Col>
        </Grid>

        <div style={{ margin: "0px 8px" }}>
          <NotesList
            notes={notesData?.notesMany || []}
            onRefetch={refetchNotes}
            clientId={client?.id}
            roleType={EMPLOYEE_ROLE_OBJ.checkInQueue}
          />
        </div>
      </ScrollArea>
    </>
  );
}

export default memo(LeftSide);
